import { create } from 'zustand';
import { apiErrorMessage } from '@/utils/apiError';
import axios from '@/utils/axios';
import { orderConversations, turnAfter } from '@/utils/conversationOrder';
import { isHiddenFor } from '@/utils/hiddenFor';


/**
 * Global Messages Store using Zustand
 *
 * This store provides:
 * - Centralized state for conversations and unread counts
 * - Real-time updates from socket events
 * - Polling fallback for reliability
 * - Visibility API integration
 */
const useMessagesStore = create((set, get) => ({
  // State
  conversations: [],
  unreadCount: 0,
  unreadByConversation: {},
  loading: false,
  lastFetch: null,
  error: null,
  // Verification cache: conversationId -> { data, timestamp }
  verificationCache: {},
  // Prefetch cache: conversationId -> { messages, conversation, verification, timestamp }
  prefetchCache: {},
  // Track in-flight prefetch requests to avoid duplicates
  prefetchingIds: new Set(),

  // Actions

  /**
   * Fetch all conversations and unread counts from API
   */
  fetchConversations: async () => {
    const { loading, lastFetch } = get();

    // Debounce: Don't fetch if last fetch was less than 2 seconds ago
    if (loading || (lastFetch && Date.now() - lastFetch < 2000)) {
      return;
    }

    set({ loading: true, error: null });

    try {
      const [conversationsRes, unreadRes] = await Promise.all([
        axios.get(`/conversations`),
        axios.get(`/conversations/unread-count`).catch(() => ({ data: { unread_count: 0, unread_by_conversation: {} } }))
      ]);

      set({
        conversations: conversationsRes.data.conversations || [],
        unreadCount: unreadRes.data.unread_count || 0,
        unreadByConversation: unreadRes.data.unread_by_conversation || {},
        loading: false,
        lastFetch: Date.now()
      });
    } catch (error) {
      set({
        loading: false,
        error: apiErrorMessage(error, 'Failed to fetch conversations')
      });
    }
  },

  /**
   * Fetch only unread counts (lighter than full fetch)
   */
  fetchUnreadCount: async () => {
    try {
      const response = await axios.get(`/conversations/unread-count`);

      set({
        unreadCount: response.data.unread_count || 0,
        unreadByConversation: response.data.unread_by_conversation || {}
      });
    } catch (error) {
      // Silently fail for unread count fetch
    }
  },

  // Track processed message IDs to prevent duplicates
  processedMessageIds: new Set(),

  /**
   * Handle new message from socket
   * Increments unread count and updates conversation
   */
  handleNewMessage: (message, currentUserId) => {
    const { conversations, unreadCount, unreadByConversation, processedMessageIds } = get();

    // Only count if we're the receiver
    if (message.receiver_id !== currentUserId) return;

    // Skip if conversation not in our list (e.g. already removed by conversation_closed)
    const convExists = conversations.some(c => c.id === message.conversation_id);
    if (!convExists) return;

    // Deduplicate - don't count the same message twice
    const messageId = message.id || message._id;
    if (messageId && processedMessageIds.has(messageId)) {
      console.log('[MessagesStore] Skipping duplicate message:', messageId);
      return;
    }

    // Mark this message as processed
    if (messageId) {
      const newProcessedIds = new Set(processedMessageIds);
      newProcessedIds.add(messageId);
      // Keep only last 100 IDs to prevent memory leak
      if (newProcessedIds.size > 100) {
        const idsArray = Array.from(newProcessedIds);
        idsArray.splice(0, idsArray.length - 100);
        set({ processedMessageIds: new Set(idsArray) });
      } else {
        set({ processedMessageIds: newProcessedIds });
      }
    }

    // Update unread counts
    const newUnreadByConversation = {
      ...unreadByConversation,
      [message.conversation_id]: (unreadByConversation[message.conversation_id] || 0) + 1
    };

    // Update conversation's last message time and move to top. Also bump
    // unread_count on the conversation object itself — the sidebar reads this
    // field directly (markConversationAsRead below explicitly clears it to 0),
    // but nothing was ever incrementing it, only the separate
    // unreadByConversation map — so the sidebar badge only became correct
    // again after a full refetch, not live.
    const updatedConversations = conversations.map(conv => {
      if (conv.id === message.conversation_id) {
        return {
          ...conv,
          last_message_at: message.created_at || new Date().toISOString(),
          last_message: message,
          unread_count: (conv.unread_count || 0) + 1,
          is_my_turn: turnAfter(conv, message, currentUserId),
        };
      }
      return conv;
    });

    // Chats waiting on you first, then most recent first - the server's order.
    set({
      conversations: orderConversations(updatedConversations),
      unreadCount: unreadCount + 1,
      unreadByConversation: newUnreadByConversation
    });
  },

  /**
   * A message this member sent from ANOTHER device or tab (BUG-633): the row
   * shows the new last line and leaves "your turn". The chat list kept the
   * old line until a refetch, because only the sending device and one with
   * that chat open were told. Their own message, so never unread.
   */
  handleOwnMessage: (message, currentUserId) => {
    const { conversations } = get();
    if (!message?.conversation_id || isHiddenFor(message, currentUserId)) return;
    if (!conversations.some(c => c.id === message.conversation_id)) return;
    const updatedConversations = conversations.map(conv => (
      conv.id === message.conversation_id
        ? {
          ...conv,
          last_message_at: message.created_at || new Date().toISOString(),
          last_message: message,
          is_my_turn: turnAfter(conv, message, currentUserId),
        }
        : conv
    ));
    set({ conversations: orderConversations(updatedConversations) });
  },

  /**
   * Mark a conversation as read
   * Called when user clicks on a conversation
   */
  markConversationAsRead: (conversationId) => {
    const { unreadCount, unreadByConversation, conversations } = get();

    const convUnread = unreadByConversation[conversationId] || 0;
    const target = conversations.find(c => c.id === conversationId);
    const alreadyRead = !convUnread && !(conversationId in unreadByConversation)
      && (!target || !target.unread_count);

    // Bail out when there is nothing to clear.
    //
    // This ran on every conversation open AND every incoming message for the
    // open conversation, and `.map()` returns a NEW array every time — so
    // `conversations` changed identity constantly even when not one field
    // differed. Every effect subscribed to it re-ran for nothing, and
    // MessagesPage has several, including the deep-link effect and the
    // "clear if removed from the store" effect.
    //
    // Not claimed as the cause of the FRONTEND-4/5 render loop — that was
    // never reproduced. This is state churn that is wrong on its own terms,
    // and churn is what a loop feeds on.
    if (alreadyRead) return;

    const newUnreadByConversation = { ...unreadByConversation };
    delete newUnreadByConversation[conversationId];

    set({
      unreadCount: Math.max(0, unreadCount - convUnread),
      unreadByConversation: newUnreadByConversation,
      // Also clear unread_count on the conversation object (for sidebar badge)
      conversations: conversations.map(conv =>
        conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
      )
    });
  },

  /**
   * Add a new conversation to the list
   */
  addConversation: (conversation) => {
    const { conversations } = get();

    // Check if already exists
    if (conversations.some(c => c.id === conversation.id)) {
      return;
    }

    set({
      conversations: [conversation, ...conversations]
    });
  },

  /**
   * Remove a conversation from the list (e.g. when closed respectfully)
   */
  removeConversation: (conversationId) => {
    const { conversations } = get();
    set({
      conversations: conversations.filter(c => c.id !== conversationId)
    });
  },

  /**
   * Update a specific conversation
   */
  updateConversation: (conversationId, updates) => {
    const { conversations } = get();

    set({
      conversations: orderConversations(conversations.map(conv =>
        conv.id === conversationId ? { ...conv, ...updates } : conv
      ))
    });
  },

  /**
   * Cache verification status for a conversation
   * Cache is valid for 5 minutes
   */
  setVerificationStatus: (conversationId, data) => {
    const { verificationCache } = get();
    set({
      verificationCache: {
        ...verificationCache,
        [conversationId]: {
          data,
          timestamp: Date.now()
        }
      }
    });
  },

  /**
   * Get cached verification status for a conversation
   * Returns null if cache is stale (> 5 minutes) or not found
   */
  getVerificationStatus: (conversationId) => {
    const { verificationCache } = get();
    const cached = verificationCache[conversationId];

    if (!cached) return null;

    // Cache valid for 5 minutes
    const CACHE_TTL = 5 * 60 * 1000;
    if (Date.now() - cached.timestamp > CACHE_TTL) {
      return null;
    }

    return cached.data;
  },

  /**
   * Clear verification cache for a specific conversation
   */
  clearVerificationCache: (conversationId) => {
    const { verificationCache } = get();
    const newCache = { ...verificationCache };
    delete newCache[conversationId];
    set({ verificationCache: newCache });
  },

  /**
   * Prefetch messages for a conversation on hover
   * This makes clicking feel instant because data is already loaded
   */
  prefetchConversation: async (conversationId) => {
    if (!conversationId) return;

    const { prefetchCache, prefetchingIds } = get();

    // Check if already cached (valid for 2 minutes)
    const cached = prefetchCache[conversationId];
    if (cached && Date.now() - cached.timestamp < 2 * 60 * 1000) {
      return; // Already cached
    }

    // Check if already fetching
    if (prefetchingIds.has(conversationId)) {
      return;
    }

    // Mark as fetching
    const newPrefetchingIds = new Set(prefetchingIds);
    newPrefetchingIds.add(conversationId);
    set({ prefetchingIds: newPrefetchingIds });

    try {
      const response = await axios.get(
        `/conversations/${conversationId}/messages?limit=50&include_verification=true`
      );

      // Store in cache
      set({
        prefetchCache: {
          ...get().prefetchCache,
          [conversationId]: {
            messages: response.data.messages,
            conversation: response.data.conversation,
            verification: response.data.verification,
            pagination: response.data.pagination,
            timestamp: Date.now()
          }
        }
      });

      // Also cache verification status
      if (response.data.verification) {
        get().setVerificationStatus(conversationId, response.data.verification);
      }
    } catch (error) {
      // Silently fail - prefetch is optional optimization
      console.debug('Prefetch failed:', error.message);
    } finally {
      // Remove from fetching set
      const currentIds = get().prefetchingIds;
      const updatedIds = new Set(currentIds);
      updatedIds.delete(conversationId);
      set({ prefetchingIds: updatedIds });
    }
  },

  /**
   * Get prefetched data for a conversation
   * Returns null if not cached or cache is stale
   */
  getPrefetchedData: (conversationId) => {
    const { prefetchCache } = get();
    const cached = prefetchCache[conversationId];

    if (!cached) return null;

    // Cache valid for 2 minutes
    if (Date.now() - cached.timestamp > 2 * 60 * 1000) {
      return null;
    }

    return cached;
  },

  /**
   * Clear prefetch cache for a specific conversation
   */
  clearPrefetchCache: (conversationId) => {
    const { prefetchCache } = get();
    const newCache = { ...prefetchCache };
    delete newCache[conversationId];
    set({ prefetchCache: newCache });
  },

  /**
   * Reset store (on logout)
   */
  reset: () => {
    set({
      conversations: [],
      unreadCount: 0,
      unreadByConversation: {},
      loading: false,
      lastFetch: null,
      error: null,
      verificationCache: {},
      prefetchCache: {},
      prefetchingIds: new Set(),
      processedMessageIds: new Set()
    });
  }
}));

export default useMessagesStore;
